/**
 * Page metadata, written into the document from the page that owns it.
 *
 * ── Why this runs in an effect and still ends up in the HTML ──────────────
 * Everything here happens after React mounts, so a crawler that does not run
 * JavaScript sees none of it. That is why the build pre-renders each public
 * route with a headless browser: it loads the page, lets this effect run, and
 * saves the resulting HTML. The tags are baked by the time anything fetches it.
 * Adding a public route without adding it to scripts/prerender.mjs leaves that
 * page with the shell's defaults, which is the failure mode to watch for.
 *
 * `canonical` defaults to the current path rather than being optional-and-unset:
 * /pricing and /pricing/ served the same bytes with nothing to say which one
 * counted, which is how one page becomes two competing results.
 */

import { useEffect } from 'react';

interface SEOOptions {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonical?: string;
  /**
   * schema.org JSON-LD for this page - an object or several.
   *
   * Pass a stable value. It is serialised on every run of the effect, so an
   * object literal rebuilt each render would rewrite the tag each time.
   */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * The address this site is canonically served from.
 *
 * Hardcoded rather than read from window.location. The build pre-renders every
 * page against a throwaway local server, so taking the live origin baked
 * <link rel="canonical" href="http://127.0.0.1:5599/about"> into the HTML that
 * shipped - a canonical pointing at a machine nobody can reach, which is worse
 * than having none. A page has one true address wherever it happens to render.
 */
const SITE_ORIGIN = 'https://bloomneo.com';

/**
 * One URL shape, chosen here: no trailing slash, except the root.
 *
 * nginx serves /about and /about/ as the same page, so without this it is
 * reachable at two addresses with neither declaring itself the original.
 */
/**
 * An absolute URL for og:image, whatever was passed in.
 *
 * Open Graph scrapers do not resolve relative paths - they fetch exactly the
 * string in the tag, from their own servers, so "/og-image.png" is a request to
 * facebook.com or slack.com and returns nothing. The path was also pointing at
 * an SVG, which X, LinkedIn, Slack and WhatsApp all refuse to render, so links
 * unfurled with no picture at all.
 */
function absoluteImage(src: string): string {
  return /^https?:\/\//i.test(src) ? src : `${SITE_ORIGIN}${src.startsWith('/') ? '' : '/'}${src}`;
}

function canonicalFor(pathname: string): string {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
  return `${SITE_ORIGIN}${path}`;
}

export const useSEO = ({
  title = 'Bloomneo — Managed Infrastructure, One Monthly Bill',
  description = 'Servers, domains, certificates, databases, backups and monitoring for the software your '
    + 'business runs on — managed for you, on one monthly bill.',
  keywords = 'managed infrastructure, managed hosting, server management, ssl certificates, database backups',
  ogTitle,
  ogDescription,
  ogImage = '/og-image.png',
  canonical,
  jsonLd,
}: SEOOptions = {}) => {
  useEffect(() => {
    // Set page title
    document.title = title;

    // Helper function to set or update meta tags
    const setMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${property}"]`);

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, property);
        document.head.appendChild(meta);
      }

      meta.setAttribute('content', content);
    };

    // Set meta description
    setMetaTag('description', description);

    // Set keywords
    if (keywords) {
      setMetaTag('keywords', keywords);
    }

    // Set Open Graph tags
    setMetaTag('og:title', ogTitle || title, true);
    setMetaTag('og:description', ogDescription || description, true);
    const imageUrl = absoluteImage(ogImage);
    setMetaTag('og:image', imageUrl, true);
    setMetaTag('og:image:width', '1200', true);
    setMetaTag('og:image:height', '630', true);
    // Describes the picture, not the page: it is the same card everywhere, and
    // a title pasted in here reads as "… One Subscription — Bloomneo".
    setMetaTag('og:image:alt', 'Bloomneo — managed server infrastructure, run for you', true);
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:site_name', 'Bloomneo', true);

    // Set Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', ogTitle || title);
    setMetaTag('twitter:description', ogDescription || description);
    setMetaTag('twitter:image', imageUrl);

    /*
     * Always set, never merely accepted. Every page has one true address; a page
     * that declines to say which it is leaves the choice to the crawler.
     */
    const canonicalHref = canonical ?? canonicalFor(window.location.pathname);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalHref);
    setMetaTag('og:url', canonicalHref, true);

    /*
     * Structured data, replaced rather than appended: the tag is marked so a
     * navigation cannot leave the previous page's schema behind it.
     */
    document.querySelectorAll('script[type="application/ld+json"][data-seo="page"]').forEach((el) => el.remove());
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'page');
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    // Set viewport if not already present
    if (!document.querySelector('meta[name="viewport"]')) {
      const viewport = document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
      document.head.appendChild(viewport);
    }

  }, [title, description, keywords, ogTitle, ogDescription, ogImage, canonical, jsonLd]);
};